// SEO-optimized content for all condition-specific landing pages
// Each condition targets specific long-tail keywords for "Harrow physiotherapy"

export const conditionsData = {
  'back-pain': {
    name: 'Back Pain',
    icon: 'Activity',
    title: 'Back Pain Physiotherapy in Harrow | Lower & Upper Back Pain Treatment',
    metaDescription: 'Expert back pain physiotherapy in Harrow. Treatment for lower back pain, upper back pain, disc problems & chronic back conditions. Same-day appointments. Call 07747075809.',
    keywords: 'back pain physio Harrow, lower back pain treatment Harrow, upper back pain physiotherapy, disc problems Harrow, chronic back pain treatment',
    heroLine: 'Effective back pain treatment from a HCPC-registered, MSc-qualified physiotherapist in Harrow.',
    overview: "Back pain affects up to 80% of adults at some point in their lives and is one of the leading reasons people seek physiotherapy in Harrow. Whether it's a sudden injury, postural strain from desk work, a recurring disc issue, or chronic pain that's gone on for months, expert physiotherapy can help you understand what's driving the pain — and put together a clear plan to fix it.",
    secondPara: "At MSK and Sports Physio in Harrow, we take a thorough, evidence-based approach. We don't just chase symptoms — we identify the underlying cause through detailed assessment and combine hands-on manual therapy with progressive exercise rehabilitation so you regain strength, mobility and confidence.",
    symptoms: [
      'Persistent dull ache in the lower or upper back',
      'Sharp, stabbing pain on movement',
      'Pain radiating into the buttock or leg (sciatica)',
      'Stiffness, especially in the morning',
      'Muscle spasm or "locking up"',
      'Numbness, tingling or weakness in the legs',
    ],
    causes: [
      'Prolonged sitting and poor desk posture',
      'Disc herniation or bulge',
      'Muscle strain or ligament sprain',
      'Heavy lifting with poor technique',
      'Sports injuries (running, weight training, contact sports)',
      'Degenerative conditions (osteoarthritis, spondylosis)',
      'Stress-related muscle tension',
    ],
    treatment: [
      'Detailed biomechanical and postural assessment',
      'Hands-on manual therapy and spinal mobilisation',
      'Soft tissue release and trigger point therapy',
      'Targeted core and back strengthening programmes',
      'Movement re-education and postural correction',
      'Ergonomic and lifestyle advice (desk setup, lifting, sleeping)',
      'Pain education to break the chronic pain cycle',
    ],
    timeline: 'Most patients see meaningful improvement within 2-4 sessions for acute back pain. Chronic or disc-related conditions typically respond well within 6-8 weeks of structured rehab.',
    faqs: [
      {
        q: 'How quickly can I get a back pain physio appointment in Harrow?',
        a: "We offer same-day and next-day appointments wherever possible. Call 07747075809 or book online — we prioritise acute back pain cases.",
      },
      {
        q: 'Do I need a GP referral for back pain physiotherapy?',
        a: "No referral is needed. You can self-refer directly. We're recognised by all major UK insurers (BUPA, AXA, WPA, Vitality, Aviva) for private back pain treatment.",
      },
      {
        q: 'Will I need an MRI scan?',
        a: "Most cases of back pain don't require imaging. We'll assess thoroughly and only recommend a scan if there are specific clinical indicators. We can advise on private MRI options if needed.",
      },
      {
        q: 'Is physio safe for a slipped disc?',
        a: "Yes — physiotherapy is the first-line treatment for the vast majority of disc-related back pain. With graded loading, neural mobilisation and targeted strengthening, most patients avoid surgery entirely.",
      },
    ],
  },

  'knee-pain': {
    name: 'Knee Pain',
    icon: 'Bone',
    title: "Knee Pain Physiotherapy in Harrow | Runner's Knee, ACL & Arthritis",
    metaDescription: "Specialist knee pain physio in Harrow. Treatment for runner's knee, ACL injuries, arthritis, meniscus tears & post-op recovery. Book your assessment today.",
    keywords: "knee pain physio Harrow, runner's knee treatment, ACL rehabilitation Harrow, meniscus tear physio, knee arthritis treatment Harrow",
    heroLine: 'Expert knee physiotherapy in Harrow for athletes, runners and active individuals.',
    overview: "Knee pain is one of the most common reasons people visit a physio in Harrow. From weekend runners with patellofemoral pain to footballers with ligament injuries and adults managing osteoarthritis, our knees take an enormous amount of load every day. Without proper assessment, the wrong rehab can prolong recovery — or make things worse.",
    secondPara: "At MSK and Sports Physio, we specialise in identifying exactly what's driving your knee pain — whether it's a biomechanical issue, a structural injury, or a strength imbalance — and building a step-by-step rehab plan to get you back to full performance safely.",
    symptoms: [
      'Pain around or behind the kneecap',
      'Swelling, warmth or stiffness',
      'Clicking, popping or grinding sensations',
      "Giving way or feeling unstable",
      'Pain when going up or down stairs',
      'Pain after running, squatting or kneeling',
      'Locking — inability to fully straighten or bend',
    ],
    causes: [
      'Patellofemoral pain syndrome (runner\'s knee)',
      'ACL, MCL, PCL or LCL ligament injuries',
      'Meniscus (cartilage) tears',
      'Patellar tendinopathy (jumper\'s knee)',
      'IT band friction syndrome',
      'Osteoarthritis or chondromalacia patellae',
      'Post-surgical stiffness or weakness',
    ],
    treatment: [
      'Full lower-limb biomechanical assessment',
      'Manual therapy for joint stiffness and soft tissue restrictions',
      'Progressive loading programmes (eccentric, isometric, plyometric)',
      'Gait and running technique analysis',
      'Sports-specific return-to-play protocols',
      'Taping and bracing where appropriate',
      'Strength and conditioning to bullet-proof the joint',
    ],
    timeline: 'Patellofemoral pain typically resolves within 6-8 weeks with proper rehab. ACL post-op rehab is 6-9 months structured around your surgical protocol. Meniscus issues respond within 4-6 weeks for most.',
    faqs: [
      {
        q: 'I have ACL surgery booked — when should I start physio?',
        a: 'Ideally pre-habilitation starts 2-4 weeks before surgery. Strong, mobile knees recover much faster. We work closely with leading London orthopaedic surgeons and can match the post-op protocol exactly.',
      },
      {
        q: 'Is running causing my knee pain?',
        a: "Running rarely causes knee pain on its own — it's usually a combination of training load, biomechanics, footwear and strength gaps. We do a detailed running assessment to pinpoint the cause.",
      },
      {
        q: 'Do I need a knee brace?',
        a: "Bracing has a role for certain ligament injuries and post-surgical phases, but most knee pain doesn't require one. We'll assess and advise on a case-by-case basis.",
      },
      {
        q: 'Can physio help knee arthritis?',
        a: 'Yes — strong evidence shows that targeted strengthening reduces arthritic knee pain and improves function as effectively as surgery for many patients. We build a programme around your goals and lifestyle.',
      },
    ],
  },

  'shoulder-pain': {
    name: 'Shoulder Pain',
    icon: 'Activity',
    title: 'Shoulder Pain Physiotherapy in Harrow | Frozen Shoulder & Rotator Cuff',
    metaDescription: 'Expert shoulder pain physio in Harrow. Treatment for frozen shoulder, rotator cuff injuries, impingement & instability. Same-day appointments available.',
    keywords: 'shoulder pain physio Harrow, frozen shoulder treatment Harrow, rotator cuff rehab, shoulder impingement physiotherapy',
    heroLine: 'Specialist shoulder physiotherapy in Harrow — frozen shoulder, rotator cuff, impingement and more.',
    overview: "The shoulder is the most mobile joint in the body — which is exactly why it's so prone to pain. Whether you can't lift your arm to brush your hair, you've torn your rotator cuff in the gym, or your shoulder is mysteriously stiff and aching at night, the right physiotherapy approach can make a huge difference.",
    secondPara: "At MSK and Sports Physio in Harrow, we assess the shoulder as part of a wider system — looking at your neck, thoracic spine, scapular control and movement patterns — to find the real driver of your pain and address it at the source.",
    symptoms: [
      "Inability to lift the arm above shoulder height",
      'Pain at night, especially when lying on the affected side',
      'Sharp pain with reaching or throwing',
      'Weakness in the arm',
      'Stiffness and loss of range of motion',
      'Clicking, popping or catching',
      "Dull, aching pain in the front or side of the shoulder",
    ],
    causes: [
      'Frozen shoulder (adhesive capsulitis)',
      'Rotator cuff tear or tendinopathy',
      'Subacromial impingement',
      'Shoulder instability or dislocation',
      'Calcific tendinitis',
      'AC joint sprain or arthritis',
      'Postural and scapular dysfunction',
    ],
    treatment: [
      'Detailed shoulder, neck and thoracic spine assessment',
      'Manual therapy for joint stiffness and soft tissue work',
      'Rotator cuff strengthening progression',
      'Scapular control and stability training',
      'Stretching and capsular mobilisation',
      'Ergonomic and postural advice',
      'Sports- and work-specific return programmes',
    ],
    timeline: 'Rotator cuff strains respond in 4-6 weeks. Frozen shoulder is longer (6-12 months total recovery, but rapid pain reduction in the first 4-6 weeks of treatment). Post-op rotator cuff repair is a 4-6 month structured rehab.',
    faqs: [
      {
        q: 'Can frozen shoulder be cured with physio?',
        a: "Yes. Although frozen shoulder is self-limiting, hands-on physio dramatically reduces pain and accelerates recovery of movement. Without treatment it can drag on for 18-24 months.",
      },
      {
        q: 'Do I need an MRI for shoulder pain?',
        a: "Most shoulder problems can be assessed clinically. We only recommend imaging when there's a strong suspicion of a full-thickness tear or surgical pathology.",
      },
      {
        q: 'Will a steroid injection help?',
        a: "Injections can be useful in specific cases (severe frozen shoulder, persistent impingement) — but the best outcomes come from combining injection with structured physio. We can advise and refer if needed.",
      },
    ],
  },

  'neck-pain': {
    name: 'Neck Pain',
    icon: 'Stethoscope',
    title: 'Neck Pain Physiotherapy in Harrow | Whiplash, Stiffness & Headaches',
    metaDescription: 'Expert neck pain physio in Harrow. Treatment for whiplash, cervical pain, tension headaches & postural neck pain. HCPC registered. Book today.',
    keywords: 'neck pain physio Harrow, whiplash treatment Harrow, cervical pain physiotherapy, tension headaches Harrow',
    heroLine: 'Effective neck pain treatment in Harrow for whiplash, stiffness and stress-related tension.',
    overview: "Neck pain has become almost an epidemic — driven by long hours at the desk, phone use, and the stress of modern life. The good news? It usually responds very well to skilled physiotherapy. Whether your neck pain is from a recent whiplash injury, chronic stiffness, or causes tension headaches, we can help.",
    secondPara: "At MSK and Sports Physio in Harrow, we take time to understand exactly what's causing your symptoms — including how your work, sleep, and stress patterns are contributing — and we build a focused plan to ease pain, restore movement and prevent it returning.",
    symptoms: [
      'Stiffness when turning the head',
      'Pain at the base of the skull or upper shoulders',
      'Headaches at the back of the head (cervicogenic)',
      'Arm pain, pins and needles or numbness',
      'Dizziness or visual disturbance',
      'Difficulty sleeping due to neck pain',
      'Sharp pain with specific movements',
    ],
    causes: [
      'Whiplash from road traffic accidents',
      'Poor desk and screen posture',
      'Cervical spondylosis (age-related wear)',
      'Disc herniation in the cervical spine',
      'Muscle strain or "wry neck"',
      'Stress and muscle tension',
      'Sleeping position and pillow problems',
    ],
    treatment: [
      'Detailed cervical and thoracic spine assessment',
      'Manual therapy and joint mobilisation',
      'Soft tissue release for tight neck and shoulder muscles',
      'Deep neck flexor strengthening',
      'Postural retraining and ergonomic advice',
      'Stress and pain management strategies',
      'Pillow and sleeping position advice',
    ],
    timeline: 'Acute neck pain and whiplash typically resolve in 2-4 weeks. Chronic neck pain responds in 6-8 weeks with consistent rehab and postural change.',
    faqs: [
      {
        q: 'Can physio help with tension headaches?',
        a: "Yes. Many headaches are actually cervicogenic — driven by neck dysfunction. Treating the cervical spine and upper traps often resolves headaches that medication can't.",
      },
      {
        q: 'Should I use heat or ice for neck pain?',
        a: "For acute injury (first 48 hours), ice helps. For chronic stiffness, heat is more effective. We'll guide you on what works best for your specific case.",
      },
      {
        q: 'Is my pillow causing neck pain?',
        a: "It could be a factor. We assess your sleeping position and can advise on pillow type and height to support recovery.",
      },
    ],
  },

  'acl-injuries': {
    name: 'ACL Injuries',
    icon: 'Zap',
    title: 'ACL Injury Rehabilitation in Harrow | Pre-Op & Post-Op Physio',
    metaDescription: 'Specialist ACL injury physiotherapy in Harrow. Pre-op prehab & post-op rehabilitation. Surgeon-aligned protocols. MSc qualified sports physio.',
    keywords: 'ACL physio Harrow, ACL surgery rehab Harrow, ACL reconstruction rehabilitation, ligament injury treatment',
    heroLine: 'Surgeon-aligned ACL rehabilitation in Harrow — get back to sport stronger than before.',
    overview: "An ACL injury is a major event for any athlete or active person. Whether you're managing it conservatively or recovering from reconstruction surgery, the quality of your rehab is the single biggest predictor of how well — and how quickly — you return to your sport.",
    secondPara: "At MSK and Sports Physio in Harrow, ACL rehabilitation is one of our specialist areas. We follow your surgeon's protocol precisely, work with leading London orthopaedic consultants, and structure your rehab through clear phases — from early protection to return-to-sport testing.",
    symptoms: [
      'A "pop" at the moment of injury',
      'Immediate swelling within hours',
      'Knee instability or "giving way"',
      'Inability to fully straighten or bend the knee',
      'Pain with weight-bearing',
      'Bruising and stiffness',
      'A feeling that the knee "isn\'t the same" weeks later',
    ],
    causes: [
      'Sudden change of direction (football, netball, basketball, rugby)',
      'Landing awkwardly from a jump',
      'Direct contact injury',
      'Skiing and snowboarding accidents',
      'Combination injuries (ACL + meniscus + MCL)',
    ],
    treatment: [
      'Pre-operative "prehab" to optimise surgical outcomes',
      'Early-phase post-op rehab (weeks 0-6): swelling control, ROM, gentle strength',
      'Mid-phase (weeks 6-16): progressive loading, neuromuscular control',
      'Late-phase (months 4-9): plyometrics, cutting, agility, sport-specific drills',
      'Return-to-sport testing: hop testing, strength symmetry, sport-specific testing',
      'Re-injury prevention strategies',
      'Direct communication with your surgeon throughout',
    ],
    timeline: 'Pre-op prehab: 2-6 weeks pre-surgery. Post-op return to running: 12-16 weeks. Return to full sport: 9-12 months depending on sport and graft type.',
    faqs: [
      {
        q: 'Do I need surgery for an ACL tear?',
        a: 'Not always. For non-pivoting sports and certain partial tears, structured conservative rehab can be highly effective. We do thorough functional testing to advise on the best option for you.',
      },
      {
        q: 'How soon should I start physio after ACL surgery?',
        a: "Ideally within the first week. Early controlled movement and swelling management dramatically improve outcomes. We can liaise with your surgeon to start immediately.",
      },
      {
        q: 'When can I return to football after ACL reconstruction?',
        a: 'Most patients return to football 9-12 months post-op, but only after passing strength and functional testing. Returning too early is the #1 cause of re-rupture.',
      },
    ],
  },

  'sports-injuries': {
    name: 'Sports Injuries',
    icon: 'Activity',
    title: 'Sports Injury Physiotherapy in Harrow | Running, Football, Tennis & More',
    metaDescription: 'Expert sports injury physio in Harrow. Treatment for runners, footballers, tennis players, gym injuries. MSc Sports Medicine. Same-day appointments.',
    keywords: 'sports injury physio Harrow, running injury treatment Harrow, sports physiotherapy Harrow, tennis elbow Harrow, football injury physio',
    heroLine: 'Specialist sports injury physiotherapy in Harrow from an MSc Sports Medicine practitioner.',
    overview: "Sports injuries demand more than generic physiotherapy. Whether you're a competitive athlete, weekend warrior, or just back into running, the right rehabilitation programme is the difference between a quick return to performance and months of frustration.",
    secondPara: "At MSK and Sports Physio in Harrow, our founder Param holds an MSc in Sports Medicine and has worked with athletes across football, running, tennis, climbing, rugby and more. We don't just rest you — we build you back stronger with sport-specific testing and return-to-play criteria.",
    symptoms: [
      'Acute pain during or after activity',
      'Bruising, swelling or visible deformity',
      'Restricted range of motion',
      'Difficulty bearing weight or using the limb',
      'Recurring pain in the same area',
      'Pain that doesn\'t improve with rest',
      'A "pop" or "snap" sensation at injury',
    ],
    causes: [
      'Running injuries (shin splints, plantar fasciitis, IT band pain)',
      'Football injuries (hamstring strains, ACL, ankle sprains)',
      'Tennis and racket sports (tennis elbow, shoulder impingement)',
      'Climbing injuries (pulley injuries, shoulder, elbow tendinopathy)',
      'Gym injuries (lower back, shoulder, knee)',
      'Rugby and contact sports (concussion, joint injuries)',
      'Overtraining and overuse',
    ],
    treatment: [
      'Sport-specific assessment and diagnosis',
      'Acute injury management (RICE, taping, immobilisation)',
      'Manual therapy and soft tissue work',
      'Progressive loading and tissue capacity building',
      'Movement analysis (running gait, throwing mechanics, etc.)',
      'Sport-specific functional drills',
      'Return-to-sport testing and clearance',
      'Injury prevention programmes',
    ],
    timeline: 'Most acute soft tissue injuries: 2-6 weeks. Tendinopathies: 6-12 weeks. Complex injuries (ACL, fractures): 3-9 months structured rehab.',
    faqs: [
      {
        q: 'I have an event coming up — can you help me train through this injury?',
        a: "Often yes. Smart load management lets many athletes continue training while rehabbing. We'll assess what's safe and structure your rehab around your event.",
      },
      {
        q: 'Do you work with running gait analysis?',
        a: "Yes. We do detailed running assessment to identify biomechanical factors driving recurring injuries — and prescribe specific drills to fix them.",
      },
      {
        q: 'Can I use insurance for sports physio?',
        a: "Yes. We're recognised by BUPA, AXA, WPA, Vitality, Aviva and other major UK insurers. Just bring your authorisation code.",
      },
    ],
  },

  'sciatica': {
    name: 'Sciatica',
    icon: 'Footprints',
    title: 'Sciatica Physiotherapy in Harrow | Nerve Pain & Leg Pain Treatment',
    metaDescription: 'Specialist sciatica physio in Harrow. Treatment for sciatic nerve pain, leg pain, numbness & tingling. Evidence-based rehab. Book today.',
    keywords: 'sciatica physio Harrow, sciatic nerve pain treatment, leg pain physiotherapy Harrow, nerve pain treatment',
    heroLine: 'Effective sciatica treatment in Harrow — addressing the root cause, not just the symptoms.',
    overview: "Sciatica is the term for pain that radiates from the lower back down through the buttock and into the leg, caused by irritation of the sciatic nerve. It can range from a mild ache to debilitating sharp pain with numbness and weakness. The good news: the vast majority of sciatica cases resolve with skilled physiotherapy — surgery is rarely needed.",
    secondPara: "At MSK and Sports Physio in Harrow, we identify exactly what's irritating your sciatic nerve — whether it's a disc bulge, piriformis tightness, or referred pain from the lumbar spine — and treat the underlying cause systematically.",
    symptoms: [
      'Sharp, burning or shooting pain down one leg',
      'Pain that worsens with sitting or coughing',
      'Numbness or tingling in the leg or foot',
      'Weakness in the leg or foot drop',
      'Pain in the lower back, buttock and back of thigh',
      'Difficulty standing up from sitting',
      'Pain worse in the morning or after prolonged sitting',
    ],
    causes: [
      'Lumbar disc herniation or bulge',
      'Spinal stenosis (narrowing of the spinal canal)',
      'Piriformis syndrome (muscle compressing the nerve)',
      'Sacroiliac joint dysfunction',
      'Degenerative disc disease',
      'Pregnancy-related sciatica',
      'Muscle spasm referring pain',
    ],
    treatment: [
      'Detailed neurological and orthopaedic assessment',
      'Neural mobilisation techniques (nerve flossing)',
      'Lumbar spine manual therapy and mobilisation',
      'Specific exercise prescription (McKenzie method where appropriate)',
      'Core stability and glute strengthening',
      'Postural and ergonomic correction',
      'Pain education and self-management strategies',
    ],
    timeline: 'Acute sciatica typically improves significantly within 2-4 weeks. Chronic cases respond within 6-12 weeks. Less than 5% require surgical intervention.',
    faqs: [
      {
        q: 'Will I need an MRI for sciatica?',
        a: "Most sciatica improves without imaging. We only refer for MRI if symptoms are severe, persistent, or there are red-flag signs. Many people have disc bulges on MRI without any pain — clinical examination is more useful.",
      },
      {
        q: 'Should I rest or stay active with sciatica?',
        a: 'Stay as active as your pain allows. Total bed rest actually slows recovery. We\'ll guide you through what movements help versus those that aggravate the nerve.',
      },
      {
        q: 'Can sciatica be cured permanently?',
        a: "In most cases, yes — provided we identify and address the underlying cause, build robust strength and mobility, and you maintain the long-term habits we set up together. Recurrence is uncommon but not impossible, especially if lifestyle factors (heavy lifting, prolonged sitting, deconditioning) return. Some chronic or structural cases (e.g., severe spinal stenosis) may need ongoing management.",
      },
    ],
  },

  'post-operative-recovery': {
    name: 'Post-Operative Recovery',
    icon: 'Stethoscope',
    title: 'Post-Operative Physiotherapy in Harrow | Knee, Hip & Shoulder Surgery Rehab',
    metaDescription: 'Expert post-op physio in Harrow. Joint replacement, ACL, rotator cuff & spinal surgery rehabilitation. Surgeon-aligned protocols. Book your assessment.',
    keywords: 'post op physio Harrow, post-operative rehabilitation, knee replacement rehab Harrow, hip replacement physio, surgery rehab Harrow',
    heroLine: 'Surgeon-aligned post-operative rehabilitation in Harrow to get you back to full function.',
    overview: "Post-operative rehabilitation is what determines whether a successful surgery actually translates to a successful outcome for you. Whether you've had a knee or hip replacement, ACL reconstruction, rotator cuff repair or spinal surgery, the quality and consistency of your physio in the weeks and months that follow is critical.",
    secondPara: "At MSK and Sports Physio in Harrow, we specialise in post-surgical rehabilitation. We work directly with leading London orthopaedic surgeons and follow each protocol precisely — while tailoring the rehab to your individual goals, whether that's walking pain-free, returning to sport, or just getting back to daily life.",
    symptoms: [
      'Post-surgical pain and swelling',
      'Stiffness and loss of range of motion',
      'Muscle weakness from disuse',
      'Difficulty with normal daily activities',
      'Scar tissue and adhesions',
      'Anxiety about loading the operated area',
      'Compensatory pain elsewhere',
    ],
    causes: [
      'Knee replacement (total or partial)',
      'Hip replacement',
      'ACL / PCL / meniscus surgery',
      'Rotator cuff repair',
      'Shoulder stabilisation surgery',
      'Spinal surgery (discectomy, fusion, laminectomy)',
      'Hand and wrist surgery',
      'Ankle and foot surgery',
    ],
    treatment: [
      'Phased rehabilitation aligned to surgeon\'s protocol',
      'Swelling and pain management',
      'Scar tissue mobilisation',
      'Progressive range of motion work',
      'Strengthening through full ROM',
      'Functional and sport-specific retraining',
      'Return-to-work and return-to-sport testing',
      'Communication with your surgical team',
    ],
    timeline: 'Hip / knee replacement: 3-6 months. ACL reconstruction: 9-12 months. Rotator cuff repair: 4-6 months. Spinal surgery: 3-9 months depending on procedure.',
    faqs: [
      {
        q: 'When should I start physio after surgery?',
        a: "As soon as your surgeon allows — often within the first week. Early controlled rehab dramatically improves long-term outcomes. We can liaise with your surgeon to align timing.",
      },
      {
        q: 'Will my insurance cover post-op physio?',
        a: "Most private medical insurance covers post-op rehab when the surgery was authorised. We work with BUPA, AXA, WPA, Vitality, Aviva and more.",
      },
      {
        q: 'How many physio sessions will I need?',
        a: "It varies by procedure. Knee replacement typically needs 8-12 sessions; ACL reconstruction 15-20 sessions. We give you a clear plan upfront.",
      },
    ],
  },

  'hip-pain': {
    name: 'Hip Pain',
    icon: 'Bone',
    title: 'Hip Pain Physiotherapy in Harrow | Arthritis, Bursitis & Labral Tears',
    metaDescription: 'Expert hip pain physio in Harrow. Treatment for hip arthritis, trochanteric bursitis, labral tears, FAI & groin pain. Same-day appointments.',
    keywords: 'hip pain physio Harrow, hip arthritis treatment Harrow, hip labral tear physiotherapy, groin pain treatment',
    heroLine: 'Specialist hip physiotherapy in Harrow for arthritis, bursitis, labral tears and groin pain.',
    overview: "Hip pain affects everything from how you walk to how you sleep. Whether it's hip arthritis slowly limiting your daily life, trochanteric bursitis on the side of the hip, a sports-related labral tear, or persistent groin pain, hip problems rarely improve on their own — they need targeted intervention.",
    secondPara: "At MSK and Sports Physio in Harrow, we assess the hip as part of a wider chain — looking at your lower back, pelvis, and lower limbs — to identify the real driver. Whether the goal is to avoid surgery, prepare for surgery, or recover from it, we'll build a programme that fits.",
    symptoms: [
      'Pain in the groin, side of the hip, or buttock',
      'Pain on walking, especially uphill or stairs',
      'Stiffness, particularly after sitting',
      'Clicking, catching or locking sensations',
      'Limited range of motion (turning, putting on shoes)',
      'Pain at night when lying on the affected side',
      'Limping or altered gait',
    ],
    causes: [
      'Hip osteoarthritis',
      'Trochanteric bursitis / greater trochanteric pain syndrome',
      'Femoroacetabular impingement (FAI)',
      'Hip labral tears',
      'Gluteal tendinopathy',
      'Groin strain or athletic pubalgia',
      'Sacroiliac joint dysfunction',
    ],
    treatment: [
      'Detailed hip, pelvis and lumbar spine assessment',
      'Manual therapy and joint mobilisation',
      'Gluteal and core strengthening',
      'Hip mobility and stability work',
      'Movement re-education and gait retraining',
      'Loading programmes for tendinopathies',
      'Sport-specific and return-to-function programmes',
    ],
    timeline: 'Hip bursitis and gluteal tendinopathy: 6-12 weeks. Hip osteoarthritis: ongoing management with clear improvements in 6-8 weeks. Pre/post hip replacement: 3-6 months.',
    faqs: [
      {
        q: 'Will I eventually need a hip replacement?',
        a: 'Not necessarily. Many people with hip arthritis manage successfully for years (or indefinitely) with structured strengthening. We focus on maximising function and delaying or avoiding surgery.',
      },
      {
        q: 'Can I keep running with hip pain?',
        a: "It depends on the cause. Some hip issues respond well to load management while running; others need a temporary pause. We'll do a running assessment and advise.",
      },
      {
        q: "Is my back pain actually from my hip?",
        a: "Often, yes. The hip and lumbar spine influence each other significantly. We assess the whole region to pinpoint the true source.",
      },
    ],
  },

  'ankle-foot-pain': {
    name: 'Ankle & Foot Pain',
    icon: 'Activity',
    title: 'Ankle & Foot Pain Physiotherapy in Harrow | Sprains, Plantar Fasciitis & Achilles',
    metaDescription: 'Expert ankle and foot physio in Harrow. Treatment for sprains, plantar fasciitis, Achilles tendinopathy & flat feet. MSc Sports Medicine qualified.',
    keywords: 'ankle pain physio Harrow, plantar fasciitis treatment Harrow, Achilles tendinopathy physio, foot pain treatment',
    heroLine: 'Expert ankle and foot physiotherapy in Harrow — back on your feet, pain-free.',
    overview: "Your feet take roughly 5,000-7,000 steps a day — that's a lot of load. When something goes wrong, it affects everything from your walking to your sleep. Ankle sprains, plantar fasciitis, Achilles problems and foot pain are some of the most common reasons people see a physio in Harrow.",
    secondPara: "At MSK and Sports Physio, we provide expert assessment and treatment for the full range of ankle and foot conditions. We combine hands-on therapy with progressive loading, gait analysis and footwear advice to fully resolve the problem — not just settle it temporarily.",
    symptoms: [
      'Heel pain, especially first thing in the morning',
      'Pain at the back of the ankle (Achilles)',
      'Swelling, bruising or instability after a twist',
      'Pain in the arch of the foot',
      'Pain when walking or running',
      'Stiffness in the ankle joint',
      'Numbness, tingling or burning in the foot',
    ],
    causes: [
      'Lateral ankle sprain (most common)',
      'Plantar fasciitis',
      'Achilles tendinopathy or tear',
      'Posterior tibial tendon dysfunction',
      'Stress fractures',
      "Morton's neuroma",
      'Flat feet / overpronation',
      'Sever\'s disease (in adolescents)',
    ],
    treatment: [
      'Detailed foot, ankle and lower-limb biomechanical assessment',
      'Manual therapy and joint mobilisation',
      'Progressive loading for tendons (Alfredson, heavy slow resistance)',
      'Gait and running analysis',
      'Calf and intrinsic foot strengthening',
      'Taping and bracing techniques',
      'Footwear and orthotics advice',
    ],
    timeline: 'Acute ankle sprain: 2-6 weeks. Plantar fasciitis: 6-12 weeks. Achilles tendinopathy: 8-12 weeks of progressive loading.',
    faqs: [
      {
        q: 'How long does an ankle sprain take to heal?',
        a: "A mild sprain takes 2-3 weeks; moderate 3-6 weeks; severe (grade 3) can take 3+ months. Proper early rehab dramatically reduces recurrence — chronic ankle instability is the main long-term risk of poorly managed sprains.",
      },
      {
        q: 'Do I need custom orthotics?',
        a: "Sometimes — but rarely the first answer. Most foot and ankle issues respond brilliantly to strengthening, mobility work and gait retraining. We assess and advise on a case-by-case basis.",
      },
      {
        q: 'Can plantar fasciitis really go away?',
        a: 'Yes. With the right progressive loading programme, the vast majority of plantar fasciitis cases fully resolve within 8-12 weeks. Steroid injections and surgery are rarely needed.',
      },
    ],
  },

  'tennis-elbow': {
    name: "Tennis Elbow & Golfer's Elbow",
    icon: 'Zap',
    title: "Tennis Elbow Physiotherapy in Harrow | Lateral & Medial Epicondylitis",
    metaDescription: "Specialist tennis elbow and golfer's elbow physio in Harrow. Evidence-based treatment using eccentric loading. HCPC registered. Book today.",
    keywords: 'tennis elbow physio Harrow, golfer\'s elbow treatment Harrow, lateral epicondylitis physiotherapy, elbow pain Harrow',
    heroLine: "Expert treatment for tennis elbow and golfer's elbow in Harrow — back to full function pain-free.",
    overview: "Tennis elbow (lateral epicondylitis) and golfer's elbow (medial epicondylitis) are stubborn, frustrating conditions that often don't respond to rest. In fact, they get worse without active treatment. The good news: with the right loading-based rehab programme, the vast majority resolve completely within 8-12 weeks.",
    secondPara: "At MSK and Sports Physio in Harrow, we specialise in tendinopathy management. We don't just treat the elbow — we look at the whole upper limb, neck and grip patterns to address the root cause. And we use the latest evidence-based protocols (heavy slow resistance, eccentric loading) for fastest possible recovery.",
    symptoms: [
      'Pain on the outside of the elbow (tennis elbow)',
      'Pain on the inside of the elbow (golfer\'s elbow)',
      'Weak grip strength',
      'Pain when lifting a kettle, mug or laptop',
      'Pain with shaking hands',
      'Pain that radiates into the forearm',
      'Stiffness in the morning',
    ],
    causes: [
      'Repetitive gripping (manual work, DIY, gardening)',
      'Computer / mouse / keyboard overuse',
      'Racket sports with poor technique',
      'Weightlifting with poor form',
      'Sudden increase in activity load',
      'Underlying neck and shoulder issues',
    ],
    treatment: [
      'Detailed assessment of the elbow, wrist, shoulder and neck',
      'Manual therapy and soft tissue work',
      'Progressive eccentric and isometric loading programmes',
      'Heavy slow resistance protocols (gold standard)',
      'Grip and forearm strengthening',
      'Ergonomic and technique modification',
      'Sport-specific rehab (tennis swing, golf swing, weight training)',
    ],
    timeline: 'Most cases resolve within 8-12 weeks with consistent loading. Chronic cases (12+ months of pain) may take 4-6 months.',
    faqs: [
      {
        q: "I've had tennis elbow for a year — can it still be fixed?",
        a: 'Yes. Even very chronic cases respond to heavy slow resistance and eccentric loading. It just takes longer (3-6 months) and consistent effort.',
      },
      {
        q: 'Should I wear a tennis elbow strap?',
        a: "A strap can give short-term pain relief but doesn't treat the cause. Use it for activity if needed, but the real fix is progressive loading.",
      },
      {
        q: "I don't play tennis — why do I have tennis elbow?",
        a: 'Tennis elbow is a misnomer. Around 90% of cases are from other activities — desk work, lifting, manual jobs, gardening. The name just describes the type of tendon involved.',
      },
    ],
  },

  'chronic-pain-management': {
    name: 'Chronic Pain Management',
    icon: 'Stethoscope',
    title: 'Chronic Pain Physiotherapy in Harrow | Long-Term Pain Management',
    metaDescription: 'Specialist chronic pain physio in Harrow. Evidence-based biopsychosocial approach to long-term pain conditions, fibromyalgia & persistent pain.',
    keywords: 'chronic pain physio Harrow, pain management Harrow, persistent pain treatment, fibromyalgia physiotherapy',
    heroLine: 'Evidence-based chronic pain management in Harrow — taking back control of your life.',
    overview: "Chronic pain — pain that persists beyond 3 months — affects an estimated 28 million UK adults. It's complex, often frustrating, and rarely responds to a single intervention. But with the right combined approach — addressing the physical, mental and lifestyle factors — significant improvements are absolutely possible.",
    secondPara: "At MSK and Sports Physio in Harrow, we use the biopsychosocial model for chronic pain. We help you understand what's actually happening in your body, develop practical coping strategies, gradually rebuild activity tolerance and improve your quality of life — without simply masking symptoms.",
    symptoms: [
      'Pain lasting more than 3 months',
      'Pain that\'s "spread" beyond the original area',
      'Fatigue and poor sleep',
      'Reduced exercise tolerance',
      'Anxiety or low mood around the pain',
      'Sensitivity to touch, temperature or stress',
      'Difficulty maintaining work or daily activities',
    ],
    causes: [
      'Long-standing back, neck or joint pain',
      'Fibromyalgia',
      'Complex Regional Pain Syndrome (CRPS)',
      'Failed back surgery syndrome',
      'Chronic post-traumatic pain',
      'Persistent post-operative pain',
      'Chronic widespread pain',
    ],
    treatment: [
      'Comprehensive assessment using the biopsychosocial model',
      'Pain neuroscience education',
      'Graded activity and graded exposure programmes',
      'Manual therapy and movement work',
      'Sleep hygiene and stress management strategies',
      'Pacing techniques and energy management',
      'Realistic goal setting and progress tracking',
      'Liaison with GP, pain consultants and psychologists when needed',
    ],
    timeline: 'Chronic pain management is a longer journey — typically 3-6 months for significant change. We focus on improving function and quality of life, not just chasing a pain-free state.',
    faqs: [
      {
        q: "I've tried everything for my chronic pain — can physio really help?",
        a: 'Yes — but only with the right approach. Chronic pain rarely responds to passive, hands-on-only treatment. Combining education, graded activity and movement is where the real changes happen.',
      },
      {
        q: 'Will I need to exercise through the pain?',
        a: "Not necessarily 'through' it — but yes, controlled, graded movement is critical. We start gently and build up at a pace that works for your nervous system.",
      },
      {
        q: 'Can physio help with fibromyalgia?',
        a: "Yes. Whilst there's no cure, the combination of pain education, gentle progressive activity, and lifestyle work has strong evidence for improving symptoms and function.",
      },
    ],
  },
};

export const conditionSlugs = Object.keys(conditionsData);
